import { constructQueryParamName, parseSelectFilterOptions } from '../../../util/search';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import { SCHEMA_TYPE_BOOLEAN, SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';
import { SHIPPING_PARAM, PICKUP_PARAM } from '../DeliveryFilter/deliveryFilterHelpers';
import {
  SPONSORED_PARAM,
  RATED_PARAM,
  SELLER_PARAM,
  SELLER_TYPE_VENDOR,
  SELLER_TYPE_HOBBYIST,
  SELLER_TYPE_SPONSORED,
  classifySellerType,
  normalizeSellerParam,
} from '../SellersFilter/sellersFilterHelpers';
import {
  LISTED_PARAM,
  isListedFilterActive,
  normalizeListedToken,
} from '../DateListedFilter/dateListedFilterHelpers';

const RADIX = 10;

const isParamActive = value => value === true || value === 'true';

const removeValueFromSelectParam = (rawValue, valueToRemove, schemaType, searchMode) => {
  if (!rawValue) {
    return null;
  }
  const selected = parseSelectFilterOptions(rawValue);
  const next = selected.filter(v => v !== valueToRemove);
  if (next.length === 0) {
    return null;
  }
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  return `${mode}${next.join(',')}`;
};

const parsePriceRange = priceRange => {
  const [minPrice, maxPrice] = priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  return minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

const findOptionLabel = (options, value) => {
  const match = options?.find(o => o.option === value);
  return match?.label || value;
};

const findCategoryName = (categories, id) => {
  const find = cats => {
    for (const category of cats || []) {
      if (category.id === id) {
        return category.name || id;
      }
      const nested = find(category.subcategories);
      if (nested != null) {
        return nested;
      }
    }
    return null;
  };
  return find(categories) || id;
};

const findListingTypeLabel = (listingTypes, id) => {
  const match = listingTypes?.find(lt => lt.listingType === id);
  return match?.label || id;
};

/**
 * Build removable active-filter tag descriptors from URL query params.
 *
 * @param {Object} params
 * @param {Object} params.urlQueryParams
 * @param {Object} params.config
 * @param {Object} params.intl
 * @param {Array} params.listingFieldsConfig
 * @param {Array} params.defaultFiltersConfig
 * @returns {Array<{ id: string, label: string, removeParams: Object }>}
 */
export const getActiveFilterTags = ({
  urlQueryParams = {},
  config,
  intl,
  listingFieldsConfig = [],
  defaultFiltersConfig = [],
}) => {
  const tags = [];
  const marketplaceCurrency = config?.currency;
  const listingTypes = config?.listing?.listingTypes || [];
  const categories = config?.categoryConfiguration?.categories || [];
  const mainSearch = config?.search?.mainSearch;

  const listingTypeParam = constructQueryParamName('listingType', 'public');
  const listingTypeRaw = urlQueryParams[listingTypeParam];
  if (listingTypeRaw) {
    parseSelectFilterOptions(listingTypeRaw).forEach(value => {
      tags.push({
        id: `${listingTypeParam}:${value}`,
        label: findListingTypeLabel(listingTypes, value),
        removeParams: {
          [listingTypeParam]: removeValueFromSelectParam(listingTypeRaw, value, SCHEMA_TYPE_ENUM),
        },
      });
    });
  }

  const categoryParam = constructQueryParamName('categoryLevel1', 'public');
  const subcategoryParam = constructQueryParamName('categoryLevel2', 'public');
  const categoryRaw = urlQueryParams[categoryParam];
  const subcategoryRaw = urlQueryParams[subcategoryParam];
  if (categoryRaw) {
    parseSelectFilterOptions(categoryRaw).forEach(value => {
      tags.push({
        id: `${categoryParam}:${value}`,
        label: findCategoryName(categories, value),
        // Clearing a category also clears all subcategory selections (same as filter UI).
        removeParams: {
          [categoryParam]: removeValueFromSelectParam(categoryRaw, value, SCHEMA_TYPE_ENUM),
          [subcategoryParam]: null,
        },
      });
    });
  }
  if (subcategoryRaw) {
    parseSelectFilterOptions(subcategoryRaw).forEach(value => {
      tags.push({
        id: `${subcategoryParam}:${value}`,
        label: findCategoryName(categories, value),
        removeParams: {
          [subcategoryParam]: removeValueFromSelectParam(subcategoryRaw, value, SCHEMA_TYPE_ENUM),
        },
      });
    });
  }

  const priceRaw = urlQueryParams.price;
  const priceRange = priceRaw ? parsePriceRange(priceRaw) : null;
  if (priceRange) {
    const minLabel = formatCurrencyMajorUnit(intl, marketplaceCurrency, priceRange.minPrice);
    const maxLabel = formatCurrencyMajorUnit(intl, marketplaceCurrency, priceRange.maxPrice);
    tags.push({
      id: 'price',
      label: `${minLabel} – ${maxLabel}`,
      removeParams: { price: null },
    });
  }

  if (isParamActive(urlQueryParams[SHIPPING_PARAM])) {
    tags.push({
      id: SHIPPING_PARAM,
      label: intl.formatMessage({ id: 'DeliveryFilter.shippingLabel' }),
      removeParams: { [SHIPPING_PARAM]: null },
    });
  }

  if (isParamActive(urlQueryParams[PICKUP_PARAM])) {
    tags.push({
      id: PICKUP_PARAM,
      label: intl.formatMessage({ id: 'DeliveryFilter.pickupLabel' }),
      removeParams: {
        [PICKUP_PARAM]: null,
        bounds: null,
        pickupAddress: null,
        pickupOrigin: null,
        pickupRadius: null,
      },
    });
  }

  const sellerType = classifySellerType(urlQueryParams[SPONSORED_PARAM]);
  const sellerTypeLabelId =
    sellerType === SELLER_TYPE_VENDOR
      ? 'SellersFilter.allVendorListingsLabel'
      : sellerType === SELLER_TYPE_HOBBYIST
        ? 'SellersFilter.hobbyistListingsLabel'
        : sellerType === SELLER_TYPE_SPONSORED
          ? 'SellersFilter.sponsoredSellersLabel'
          : null;
  if (sellerTypeLabelId) {
    tags.push({
      id: `${SPONSORED_PARAM}:${sellerType}`,
      label: intl.formatMessage({ id: sellerTypeLabelId }),
      removeParams: { [SPONSORED_PARAM]: null },
    });
  }

  if (isParamActive(urlQueryParams[RATED_PARAM])) {
    tags.push({
      id: RATED_PARAM,
      label: intl.formatMessage({ id: 'SellersFilter.ratedSellersLabel' }),
      removeParams: { [RATED_PARAM]: null },
    });
  }

  const sellerUsername = normalizeSellerParam(urlQueryParams[SELLER_PARAM]);
  if (sellerUsername) {
    tags.push({
      id: SELLER_PARAM,
      label: sellerUsername,
      removeParams: { [SELLER_PARAM]: null },
    });
  }

  if (isListedFilterActive(urlQueryParams[LISTED_PARAM])) {
    const listedToken = normalizeListedToken(urlQueryParams[LISTED_PARAM]);
    tags.push({
      id: `${LISTED_PARAM}:${listedToken}`,
      label: intl.formatMessage({ id: `DateListedFilter.tag${listedToken}` }),
      removeParams: { [LISTED_PARAM]: null },
    });
  }

  if (mainSearch?.searchType === 'keywords' && urlQueryParams.keywords) {
    tags.push({
      id: 'keywords',
      label: urlQueryParams.keywords,
      removeParams: { keywords: null },
    });
  }

  listingFieldsConfig.forEach(fieldConfig => {
    const { key, scope, schemaType, enumOptions, filterConfig = {} } = fieldConfig;
    const { label: sectionLabel, searchMode } = filterConfig;
    const paramName = constructQueryParamName(key, scope);
    const rawValue = urlQueryParams[paramName];
    if (!rawValue) {
      return;
    }

    if (schemaType === SCHEMA_TYPE_BOOLEAN) {
      if (isParamActive(rawValue)) {
        tags.push({
          id: paramName,
          label: sectionLabel || key,
          removeParams: { [paramName]: null },
        });
      }
      return;
    }

    parseSelectFilterOptions(rawValue).forEach(value => {
      tags.push({
        id: `${paramName}:${value}`,
        label: findOptionLabel(enumOptions, value),
        removeParams: {
          [paramName]: removeValueFromSelectParam(rawValue, value, schemaType, searchMode),
        },
      });
    });
  });

  defaultFiltersConfig.forEach(filterConfig => {
    const { key, schemaType } = filterConfig;
    if (['listingType', 'categoryLevel', 'price', 'keywords'].includes(key)) {
      return;
    }
    const paramName = key;
    const rawValue = urlQueryParams[paramName];
    if (!rawValue) {
      return;
    }

    if (schemaType === 'dates' || schemaType === 'seats') {
      tags.push({
        id: paramName,
        label: rawValue,
        removeParams: { [paramName]: null },
      });
    }
  });

  return tags;
};
